import React, { useState, useEffect } from "react";

// Basic journey information
export interface JourneyBasicInfo {
  name: string;
  description: string;
  touchpointCount: number;
}

interface JourneyBasicInfoStepProps {
  values: JourneyBasicInfo;
  onChange: (values: JourneyBasicInfo) => void;
  registerStateCollector: (collector: () => JourneyBasicInfo) => void;
}

/**
 * First step of the journey creation wizard
 * Handles collection of basic journey information: name, description, and the number of touchpoints
 */
const JourneyBasicInfoStep: React.FC<JourneyBasicInfoStepProps> = ({
  values,
  onChange,
  registerStateCollector,
}) => {
  // Keep state completely local to prevent focus loss
  const [localValues, setLocalValues] = useState<JourneyBasicInfo>(() => ({
    ...values,
  }));

  // Only sync from parent when component mounts or values change significantly
  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  // We'll use onBlur handlers for immediate validation without focus loss

  // Handle local input changes without updating parent
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    // Update only local state during typing
    if (name === "touchpointCount") {
      setLocalValues((prev) => ({
        ...prev,
        [name]: parseInt(value, 10),
      }));
    } else {
      setLocalValues((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Handle blur events to update parent for validation
  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name } = e.target;

    // Only update the parent state for the name field on blur for validation
    if (name === "name") {
      onChange({
        ...values,
        name: localValues.name,
      });
    }
  };

  // Register a state collector function that will be called by the parent
  // before navigation instead of using onBlur
  useEffect(() => {
    // Register a function that returns our current local state
    registerStateCollector(() => localValues);

    // No need to return a cleanup as the parent component will handle this
  }, [registerStateCollector, localValues]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Journey Details
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Start by providing basic information about your engagement journey.
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Journey Name
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={localValues.name}
            onChange={handleChange}
            onBlur={handleBlur}
            className="mt-1 focus:ring-brand focus:border-brand block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            placeholder="e.g., New Hire Onboarding"
            required
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700"
          >
            Description
          </label>
          <textarea
            name="description"
            id="description"
            rows={3}
            value={localValues.description}
            onChange={handleChange}
            onBlur={() => {
              // Update parent state on blur for description field
              onChange({
                ...values,
                description: localValues.description,
              });
            }}
            className="mt-1 focus:ring-brand focus:border-brand block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
            placeholder="Briefly describe the purpose of this journey"
          />
        </div>

        <div>
          <label
            htmlFor="touchpointCount"
            className="block text-sm font-medium text-gray-700"
          >
            Number of Touchpoints
          </label>
          <input
            type="number"
            name="touchpointCount"
            id="touchpointCount"
            value={localValues.touchpointCount}
            min={1}
            max={10}
            onChange={handleChange}
            onBlur={(e) => {
              const newValue = Math.min(
                Math.max(parseInt(e.target.value, 10) || 1, 1),
                10,
              );
              onChange({
                ...localValues,
                touchpointCount: newValue,
              });
            }}
            className="mt-1 block w-full px-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand focus:border-brand sm:text-sm rounded-md"
          />
          <p className="mt-2 text-sm text-gray-500">
            You'll configure each touchpoint in the following steps. Maximum 10
            touchpoints allowed.
          </p>
        </div>
      </div>
    </div>
  );
};

export default JourneyBasicInfoStep;
